import React, { useState, useEffect } from 'react';
import PlatformLayout from '@/components/layout/PlatformLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  Lock,
  Key,
  Users,
  Activity,
  TrendingUp,
  TrendingDown,
  Eye,
  Ban,
  RefreshCw,
  Clock,
  Globe,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { toast } from 'sonner';
import { usePlatformAuth } from '@/context/PlatformAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SecurityMonitoring = () => {
  const { token } = usePlatformAuth();
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [blockedIps, setBlockedIps] = useState({ suspicious_ips: [], blocked_ips: [] });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('24');
  const [blockIpDialog, setBlockIpDialog] = useState(false);
  const [blockIpForm, setBlockIpForm] = useState({ ip_address: '', reason: '', duration_hours: 24 });

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [metricsRes, alertsRes, summaryRes, timelineRes, blockedRes] = await Promise.all([
        fetch(`${API_URL}/api/platform/security/metrics`, { headers }),
        fetch(`${API_URL}/api/platform/security/alerts?hours=${timeRange}`, { headers }),
        fetch(`${API_URL}/api/platform/security/alerts/summary?hours=${timeRange}`, { headers }),
        fetch(`${API_URL}/api/platform/security/activity-timeline?hours=${timeRange}`, { headers }),
        fetch(`${API_URL}/api/platform/security/blocked-ips`, { headers })
      ]);

      if (metricsRes.ok) setMetrics(await metricsRes.json());
      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setAlerts(data.alerts || []);
      }
      if (summaryRes.ok) setAlertsSummary(await summaryRes.json());
      if (timelineRes.ok) {
        const data = await timelineRes.json();
        setTimeline(data.events || []);
      }
      if (blockedRes.ok) setBlockedIps(await blockedRes.json());
    } catch (error) {
      console.error('Error fetching security data:', error);
      toast.error('Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token, timeRange]);

  const handleBlockIp = async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform/security/block-ip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(blockIpForm)
      });

      if (res.ok) {
        toast.success('IP blocked successfully');
        setBlockIpDialog(false);
        setBlockIpForm({ ip_address: '', reason: '', duration_hours: 24 });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to block IP');
      }
    } catch (error) {
      toast.error('Failed to block IP');
    }
  };

  const handleUnblockIp = async (ip) => {
    try {
      const res = await fetch(`${API_URL}/api/platform/security/block-ip/${encodeURIComponent(ip)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('IP unblocked');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to unblock IP');
    }
  };

  const severityColors = {
    LOW: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    MEDIUM: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    HIGH: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
    CRITICAL: 'bg-red-500/10 text-red-400 border-red-500/30'
  };

  const actionIcons = {
    LOGIN_SUCCESS: <CheckCircle className="w-4 h-4 text-green-400" />,
    LOGIN_FAILED: <XCircle className="w-4 h-4 text-red-400" />,
    '2FA_ENABLED': <Shield className="w-4 h-4 text-blue-400" />,
    '2FA_FAILED': <ShieldAlert className="w-4 h-4 text-orange-400" />,
    RATE_LIMIT_EXCEEDED: <Ban className="w-4 h-4 text-red-400" />,
    SUSPICIOUS_ACTIVITY: <AlertTriangle className="w-4 h-4 text-yellow-400" />
  };

  if (loading && !metrics) {
    return (
      <PlatformLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout>
      <div className="space-y-6" data-testid="security-monitoring-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Security Monitoring</h1>
            <p className="text-slate-400">Monitor security events and manage threats</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="24" className="text-white">Last 24 hours</SelectItem>
                <SelectItem value="72" className="text-white">Last 3 days</SelectItem>
                <SelectItem value="168" className="text-white">Last 7 days</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-700"
              onClick={fetchData}
              data-testid="refresh-security"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Login Success (24h)</p>
                  <p className="text-2xl font-bold text-white">{metrics?.authentication?.login_success_24h || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Login Failed (24h)</p>
                  <p className="text-2xl font-bold text-white">{metrics?.authentication?.login_failed_24h || 0}</p>
                  {metrics?.authentication?.failure_rate > 10 && (
                    <p className="text-xs text-red-400 mt-1">
                      {metrics?.authentication?.failure_rate}% failure rate
                    </p>
                  )}
                </div>
                <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">2FA Adoption</p>
                  <p className="text-2xl font-bold text-white">{metrics?.two_factor?.adoption_rate || 0}%</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {metrics?.two_factor?.enabled_users}/{metrics?.two_factor?.total_users} users
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Active Sessions</p>
                  <p className="text-2xl font-bold text-white">{metrics?.authentication?.active_sessions || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Threat Indicators */}
        {(metrics?.threats?.rate_limit_events_24h > 0 || metrics?.threats?.suspicious_activity_24h > 0) && (
          <Card className="bg-red-500/5 border-red-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <AlertTriangle className="w-6 h-6 text-red-400" />
                <div className="flex-1">
                  <p className="text-white font-medium">Threat Detected</p>
                  <p className="text-sm text-slate-400">
                    {metrics?.threats?.rate_limit_events_24h || 0} rate limit events, {metrics?.threats?.suspicious_activity_24h || 0} suspicious activities in last 24h
                  </p>
                </div>
                <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                  Investigate
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600">Overview</TabsTrigger>
            <TabsTrigger value="timeline" className="data-[state=active]:bg-blue-600">Activity Timeline</TabsTrigger>
            <TabsTrigger value="ips" className="data-[state=active]:bg-blue-600">IP Management</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Login Failure Trend */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Login Failures (7 days)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={metrics?.trends?.daily_login_failures || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                        <YAxis stroke="#94a3b8" fontSize={12} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                          labelStyle={{ color: '#fff' }}
                        />
                        <Line type="monotone" dataKey="failures" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Failed Login Hotspots */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Failed Login Hotspots</CardTitle>
                  <CardDescription className="text-slate-400">Top IPs with failed login attempts</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(metrics?.threats?.failed_login_hotspots || []).slice(0, 5).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                        <div className="flex items-center gap-3">
                          <Globe className="w-4 h-4 text-slate-400" />
                          <span className="text-white font-mono text-sm">{item.ip}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="border-red-500/30 text-red-400">
                            {item.attempts} attempts
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:bg-red-500/10"
                            onClick={() => {
                              setBlockIpForm({ ...blockIpForm, ip_address: item.ip });
                              setBlockIpDialog(true);
                            }}
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {(!metrics?.threats?.failed_login_hotspots || metrics.threats.failed_login_hotspots.length === 0) && (
                      <p className="text-slate-500 text-center py-8">No suspicious IPs detected</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Alert Summary */}
            {alertsSummary && (
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Alert Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(severity => (
                      <div key={severity} className={`p-4 rounded-lg border ${severityColors[severity]}`}>
                        <p className="text-sm opacity-80">{severity}</p>
                        <p className="text-2xl font-bold">{alertsSummary.by_severity?.[severity] || 0}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="timeline" className="space-y-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Security Activity Timeline</CardTitle>
                <CardDescription className="text-slate-400">Recent security-related events across all organizations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {timeline.map((event, idx) => (
                    <div key={idx} className="flex items-start gap-4 p-3 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors">
                      <div className="mt-1">
                        {actionIcons[event.action] || <Activity className="w-4 h-4 text-slate-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-white">{event.action?.replace(/_/g, ' ')}</span>
                          <Badge variant="outline" className={severityColors[event.severity] || 'border-slate-600 text-slate-400'}>
                            {event.severity || 'INFO'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-400 mt-1">
                          {event.user_email || event.user_name || 'Unknown user'} • {event.ip_address}
                        </p>
                        {event.details && Object.keys(event.details).length > 0 && (
                          <p className="text-xs text-slate-500 mt-1 font-mono">
                            {JSON.stringify(event.details)}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-sm text-slate-500 whitespace-nowrap">
                        {new Date(event.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                  {timeline.length === 0 && (
                    <p className="text-center text-slate-500 py-12">No security events in the selected time range</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ips" className="space-y-4">
            <div className="flex justify-end">
              <Button
                onClick={() => setBlockIpDialog(true)}
                className="bg-red-600 hover:bg-red-700"
                data-testid="block-ip-btn"
              >
                <Ban className="w-4 h-4 mr-2" />
                Block IP
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Suspicious IPs */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Suspicious IPs (Last Hour)</CardTitle>
                  <CardDescription className="text-slate-400">IPs with 5+ failed attempts</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">IP Address</TableHead>
                        <TableHead className="text-slate-400">Attempts</TableHead>
                        <TableHead className="text-slate-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(blockedIps.suspicious_ips || []).map((ip, idx) => (
                        <TableRow key={idx} className="border-slate-700">
                          <TableCell className="text-white font-mono">{ip._id}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="border-red-500/30 text-red-400">
                              {ip.attempts}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setBlockIpForm({ ...blockIpForm, ip_address: ip._id });
                                setBlockIpDialog(true);
                              }}
                            >
                              Block
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!blockedIps.suspicious_ips || blockedIps.suspicious_ips.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-slate-500 py-8">
                            No suspicious IPs detected
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Blocked IPs */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Blocked IPs</CardTitle>
                  <CardDescription className="text-slate-400">Currently blocked IP addresses</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">IP Address</TableHead>
                        <TableHead className="text-slate-400">Reason</TableHead>
                        <TableHead className="text-slate-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(blockedIps.blocked_ips || []).map((ip, idx) => (
                        <TableRow key={idx} className="border-slate-700">
                          <TableCell className="text-white font-mono">{ip.ip_address}</TableCell>
                          <TableCell className="text-slate-400">{ip.reason}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                              onClick={() => handleUnblockIp(ip.ip_address)}
                            >
                              Unblock
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!blockedIps.blocked_ips || blockedIps.blocked_ips.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-slate-500 py-8">
                            No blocked IPs
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Block IP Dialog */}
        <Dialog open={blockIpDialog} onOpenChange={setBlockIpDialog}>
          <DialogContent className="bg-slate-800 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">Block IP Address</DialogTitle>
              <DialogDescription className="text-slate-400">
                Block an IP address from accessing the platform
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-slate-400">IP Address</label>
                <Input
                  value={blockIpForm.ip_address}
                  onChange={(e) => setBlockIpForm({ ...blockIpForm, ip_address: e.target.value })}
                  placeholder="192.168.1.1"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                  data-testid="block-ip-input"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400">Reason</label>
                <Input
                  value={blockIpForm.reason}
                  onChange={(e) => setBlockIpForm({ ...blockIpForm, reason: e.target.value })}
                  placeholder="Brute force attempt"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400">Duration (hours)</label>
                <Select
                  value={String(blockIpForm.duration_hours)}
                  onValueChange={(v) => setBlockIpForm({ ...blockIpForm, duration_hours: parseInt(v) })}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="1" className="text-white">1 hour</SelectItem>
                    <SelectItem value="6" className="text-white">6 hours</SelectItem>
                    <SelectItem value="24" className="text-white">24 hours</SelectItem>
                    <SelectItem value="168" className="text-white">7 days</SelectItem>
                    <SelectItem value="720" className="text-white">30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBlockIpDialog(false)} className="border-slate-600 text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleBlockIp} className="bg-red-600 hover:bg-red-700" data-testid="confirm-block-ip">
                Block IP
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PlatformLayout>
  );
};

export default SecurityMonitoring;
